import React, { useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import openSlotLayoutBuilderOperation from 'editor/src/store/app/operation/openSlotLayoutBuilderOperation';
import setActiveAddLayerDropdownOperation from 'editor/src/store/app/operation/setActiveAddLayerDropdownOperation';
import addNewTextToPageOperation from 'editor/src/store/design/operation/addNewTextToPageOperation';
import createConditionOperation from 'editor/src/store/design/operation/createConditionOperation';
import createReflowGroupFromSelectionOperation from 'editor/src/store/design/operation/createReflowGroupFromSelectionOperation';
import { conditionGroupChildenKey } from 'editor/src/store/design/types';
import logPersonalizationLayerActivatedOperation from 'editor/src/store/editor/operation/logPersonalizationLayerActivatedOperation';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import { logEvent } from 'editor/src/amplitude';
import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';
import useTr from 'editor/src/util/useTr';

import ControlButton from 'editor/src/component/ControlButton';
import DropDown from 'editor/src/component/DropDown';
import { DropdownPosition } from 'editor/src/component/DropDown/getPosition';
import IconAddCondition from 'editor/src/component/Icon/IconAddCondition';
import IconAddReflow from 'editor/src/component/Icon/IconAddReflow';
import IconCollections from 'editor/src/component/Icon/IconCollections';
import IconGallery from 'editor/src/component/Icon/IconGallery';
import IconLayouts from 'editor/src/component/Icon/IconLayouts';
import IconText from 'editor/src/component/Icon/IconText';
import { UploaderContext } from 'editor/src/component/Uploader/useUploader';
import WithTooltip from 'editor/src/component/WithTooltip';

import FieldGroupTooltip from './FieldGroupTooltip';
import ReflowTooltip from './ReflowTooltip';

import styles from './index.module.scss';

interface Props {
  isDropdownVisible: boolean;
  menuPosition: DropdownPosition;
  onAddFieldGroup?: () => void;
  showCollection?: boolean;
  showCondition?: boolean;
  showSlotLayout?: boolean;
  className?: string;
  isPersonalized?: boolean;
}

function AddLayerDropdown({
  isDropdownVisible,
  className,
  menuPosition,
  showCondition,
  showCollection,
  showSlotLayout,
  isPersonalized,
  onAddFieldGroup,
}: Props) {
  const { t } = useTranslation();
  const tr = useTr();
  const dispatch = useDispatch();
  const spreadIndex = useSelector(getCurrentSpreadIndex);
  const uploader = useContext(UploaderContext);
  const hasFields = useSelector(
    (state) =>
      !!state.design.designData?.spreads[spreadIndex]?.conditionGroup?.children[conditionGroupChildenKey]?.length,
  );

  const closeDropdown = () => {
    dispatch(setActiveAddLayerDropdownOperation(undefined));
  };

  const onAddTextClick = () => {
    dispatch(addNewTextToPageOperation(spreadIndex, 0, undefined, isPersonalized));
    closeDropdown();
  };

  const onAddImageClick = () => {
    dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.GALLERY));
    uploader.open();
    closeDropdown();
  };

  const newConditionRef = useRef<string>();
  const addCondition = useCallback(() => {
    const conditionId = dispatch(createConditionOperation());
    if (conditionId) {
      newConditionRef.current = conditionId;
    }

    dispatch(logPersonalizationLayerActivatedOperation('condition'));
    logEvent('condtion created', undefined);
    closeDropdown();
  }, []);

  const onAddCollection = () => {
    sendPostMessage('advancedPersonalisation.openLibrary', undefined);
    closeDropdown();
  };

  const onAddSlotLayout = () => {
    dispatch(openSlotLayoutBuilderOperation());
    logEvent('slot layout builder opened', undefined);
    closeDropdown();
  };

  const selectedCount = useSelector((state) => state.editor.selectedElementUuids.length);
  const onAddReflow = () => {
    // Default name shown to the author; translated when production keys land,
    // English fallback so it's never a raw i18n key in the UI.
    const defaultName = t('editor-reflow-group-default-name');
    const friendlyName = defaultName === 'editor-reflow-group-default-name' ? 'Reflow group' : defaultName;
    dispatch(createReflowGroupFromSelectionOperation(friendlyName));
    closeDropdown();
  };
  const addReflowLabel = (() => {
    const translated = t('editor-add-reflow');
    return translated === 'editor-add-reflow' ? 'Add reflow' : translated;
  })();

  return (
    <DropDown
      isVisible={isDropdownVisible}
      wrapperClassName={className}
      className={styles.dropDown}
      onClickOutside={closeDropdown}
      openPosition={menuPosition}
    >
      <ControlButton onClick={onAddImageClick} stopPropagation className="cy-button-add-image">
        <IconGallery className={styles.icon} />
        {t('editor-add-image')}
      </ControlButton>
      <ControlButton onClick={onAddTextClick} stopPropagation className="cy-button-add-text">
        <IconText className={styles.icon} />
        {t('editor-add-text')}
      </ControlButton>
      {showCollection && (
        <ControlButton onClick={onAddCollection} stopPropagation className="cy-button-add-collection">
          <IconCollections className={styles.icon} />
          {t('Add collection')}
        </ControlButton>
      )}
      {showCondition && (
        <ControlButton onClick={addCondition} stopPropagation className="cy-button-add-condition">
          <IconAddCondition className={styles.icon} />
          {t('Add condition')}
        </ControlButton>
      )}
      {showSlotLayout && (
        <ControlButton onClick={onAddSlotLayout} stopPropagation className="cy-button-add-slot-layout">
          <IconLayouts className={styles.icon} />
          {tr('editor-slot-layout-add', 'Add product layout')}
        </ControlButton>
      )}
      {onAddFieldGroup && (
        <WithTooltip overlay={<FieldGroupTooltip />} placement="bottom" tooltipStyle="rich">
          <ControlButton
            onClick={() => {
              onAddFieldGroup();
              closeDropdown();
            }}
            stopPropagation
            className="cy-button-add-field-group"
            disabled={!hasFields}
          >
            <IconCollections className={styles.icon} />
            {t('editor-personalization-add-field-group', 'Add field group')}
          </ControlButton>
        </WithTooltip>
      )}
      <WithTooltip overlay={<ReflowTooltip />} placement="bottom" tooltipStyle="rich">
        <ControlButton
          onClick={onAddReflow}
          stopPropagation
          className="cy-button-add-reflow"
          disabled={selectedCount < 2}
        >
          <IconAddReflow className={styles.icon} />
          {addReflowLabel}
        </ControlButton>
      </WithTooltip>
    </DropDown>
  );
}

export default React.memo(AddLayerDropdown);
